import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";
import { AUTH } from "@/constants/testIds";

export default function ResetPasswordPage() {
  const { setUser } = useAuth();
  const { lang } = useLang();
  const t = useDict(lang);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setError(t.auth.resetInvalid);
  }, [token, t]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError(lang === "fr" ? "6 caractères minimum." : "6 characters minimum.");
      return;
    }
    if (password !== confirm) {
      setError(t.auth.passwordMismatch);
      return;
    }
    setBusy(true);
    try {
      const res = await api.resetPassword(token, password);
      setUser(res.user);
      setSuccess(true);
      setTimeout(() => navigate("/profile"), 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t.auth.resetInvalid);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 pt-20 pb-24">
      <p className="eyebrow mb-4">№ 04</p>
      <h1 className="font-serif-display text-5xl mb-2">{t.auth.resetTitle}</h1>
      <p className="text-[color:var(--ink-2)] mb-10">{t.auth.resetIntro}</p>

      {success ? (
        <p className="text-sm text-[color:var(--ink-2)] mb-8">{t.auth.resetSuccess}</p>
      ) : (
        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="label-line">{t.auth.newPassword}</label>
            <input
              data-testid={AUTH.resetPassword}
              required
              type="password"
              minLength={6}
              className="input-line"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div>
            <label className="label-line">{t.auth.confirmPassword}</label>
            <input
              data-testid={AUTH.resetConfirm}
              required
              type="password"
              minLength={6}
              className="input-line"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          {error && (
            <p data-testid={AUTH.resetError} className="text-sm text-[color:var(--red)]">{error}</p>
          )}
          <button data-testid={AUTH.resetSubmit} disabled={busy || !token} className="btn-navy w-full justify-center">
            {busy ? "..." : t.auth.resetCta}
          </button>
        </form>
      )}

      <div className="mt-10">
        <Link to="/auth" className="text-xs tracking-[0.22em] uppercase underline text-[color:var(--ink-2)]">
          {t.auth.backToLogin}
        </Link>
      </div>
    </div>
  );
}
